import React from "react";
import { TextFileViewer } from "../wazawan/asset-viewer/TextFileViewer";
import "./FilePreview.css";

type FilePreviewProps = {
  url: string;
  title: string;
  fileExtension?: string;
  className?: string;
};

export const FilePreview: React.FC<FilePreviewProps> = ({
  url,
  title,
  fileExtension,
  className = "",
}) => {
  const ext = fileExtension?.toLowerCase();

  // Images
  if (
    ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp", "ico"].includes(
      ext || ""
    )
  ) {
    return (
      <div className={`file-preview image-preview ${className}`}>
        <img src={url} alt={title} className="preview-image" />
      </div>
    );
  }

  // Videos
  if (["mp4", "webm", "ogg"].includes(ext || "")) {
    return (
      <div className={`file-preview video-preview ${className}`}>
        <video controls className="preview-video">
          <source src={url} type={`video/${ext}`} />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  // Audio
  if (["mp3", "wav", "ogg", "m4a"].includes(ext || "")) {
    return (
      <div className={`file-preview audio-preview ${className}`}>
        <audio controls className="preview-audio">
          <source src={url} type={`audio/${ext}`} />
          Your browser does not support the audio tag.
        </audio>
      </div>
    );
  }

  // PDFs - use improved iframe-based viewer
  if (ext === "pdf") {
    return (
      <div className={`file-preview document-preview ${className}`}>
        <div className="pdf-viewer-container">
          <iframe
            src={`${url}#toolbar=1&navpanes=1&scrollbar=1`}
            title={title}
            className="pdf-iframe"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              minHeight: "600px",
            }}
          />
        </div>
      </div>
    );
  }

  // Word docs - use Office Online viewer
  if (ext === "docx") {
    return (
      <div className={`file-preview document-preview ${className}`}>
        <div className="pdf-viewer-container">
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
            title={title}
            className="pdf-iframe"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              minHeight: "600px",
            }}
          />
        </div>
      </div>
    );
  }

  // Text-based files
  if (
    [
      "txt",
      "md",
      "markdown",
      "json",
      "xml",
      "html",
      "htm",
      "css",
      "js",
      "ts",
      "jsx",
      "tsx",
      "csv",
    ].includes(ext || "")
  ) {
    return (
      <div className={`file-preview text-preview ${className}`}>
        <TextFileViewer url={url} fileType={ext || "txt"} />
      </div>
    );
  }

  // Fallback for other files
  return (
    <div className={`file-preview fallback-preview ${className}`}>
      <p>Preview not available for this file type.</p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="preview-link">
        Open file in new tab
      </a>
    </div>
  );
};

// Utility function to determine if a file is viewable in browser
export const isFileViewableInBrowser = (fileExtension?: string): boolean => {
  if (!fileExtension) return false;

  const ext = fileExtension.toLowerCase();

  // Images
  if (
    ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp", "ico"].includes(ext)
  )
    return true;

  // Videos
  if (["mp4", "webm", "ogg"].includes(ext)) return true;

  // Audio
  if (["mp3", "wav", "ogg", "m4a"].includes(ext)) return true;

  // Text-based files
  if (
    [
      "txt",
      "md",
      "markdown",
      "json",
      "xml",
      "html",
      "htm",
      "css",
      "js",
      "ts",
      "jsx",
      "tsx",
      "csv",
    ].includes(ext)
  )
    return true;

  // PDFs and Word docs (with iframe viewers)
  if (["pdf", "docx"].includes(ext)) return true;

  return false;
};
